import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import { CUSTOMER_EVIDENCE, ENTERPRISE_DEMO_URL, FOUNDER, GET_STARTED_URL, PILOT_NOTE, STARTUPFUEL_URL } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { CtaButton } from '../components/ui/CtaButton';
import { Eyebrow } from '../components/ui/Eyebrow';
import { FounderAvatar } from '../components/FounderAvatar';
const APPROACH = [{
  title: 'Start from the fund’s criteria',
  body: 'Assessments begin with the questions your team asks, not a generic framework.'
}, {
  title: 'Keep evidence visible',
  body: 'Findings point to the document behind them, and missing information is stated plainly.'
}, {
  title: 'Leave judgment with investors',
  body: 'Outputs are drafts for analysts and partners to verify, challenge and decide on.'
}];
export function About() {
  usePageMeta({
    title: 'About StartupFuel, the Team Behind DiligenceGPT™',
    description: 'StartupFuel builds DiligenceGPT™, AI due diligence software that helps venture funds and startup programs screen companies and prepare investment memos using their own criteria.',
    path: '/about',
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'About',
        item: canonicalUrl('/about')
      }]
    }]
  });
  return <main id="main" className="bg-ink" data-id="element-1718" data-mp="28grv6fc4rga5">
      <section aria-labelledby="about-heading" className="border-b border-hairline-soft" data-id="element-1719" data-mp="28fqffxk665m3">
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pt-14" data-id="element-1720" data-mp="2mb2egh5cyi8t">
          <nav aria-label="Breadcrumb" data-id="element-1721" data-mp="2i9h00rd6dvwi">
            <ol className="flex items-center gap-1.5 text-xs text-text-tertiary" data-id="element-1722" data-mp="2w6cdqx4dgbiw">
              <li data-id="element-1723" data-mp="2r8ax0iuf4oal"><Link to="/" className="hover:text-text-primary" data-id="element-1724" data-mp="2r8asc5jh4049">Home</Link></li>
              <li aria-hidden="true" data-id="element-1725" data-mp="2r67yfaf79mu9"><ChevronRightIcon className="h-3 w-3" data-id="element-1726" data-mp="1ov6arkbfcr4d" /></li>
              <li aria-current="page" className="text-text-secondary" data-id="element-1727" data-mp="1osjrinf68n6b">About</li>
            </ol>
          </nav>
          <div className="mt-6" data-id="element-1728" data-mp="1stvjv0pfjmi7"><Eyebrow data-id="element-1729" data-mp="1xvkvoikh847c">About</Eyebrow></div>
          <h1 id="about-heading" className="mt-4 max-w-3xl text-balance text-[38px] font-semibold leading-[1.04] tracking-[-0.035em] text-text-primary sm:text-[52px]" data-id="element-1730" data-mp="1xujbnolhg7dh">
            Helping investment teams assess more companies, more consistently.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-text-secondary sm:text-lg" data-id="element-1731" data-mp="2cjz1919a26rl">
            DiligenceGPT™ is built by{' '}
            <a href={STARTUPFUEL_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-text-primary underline underline-offset-2" data-id="element-1732" data-mp="2gmc3hfotfmjm">
              StartupFuel
            </a>
            .
          </p>
        </div>
      </section>

      <section aria-labelledby="problem-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1733" data-mp="r6mdp3bql60p">
        <div className="mx-auto grid w-full max-w-[1100px] gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8" data-id="element-1734" data-mp="jnvaayfp2zwr">
          <h2 id="problem-heading" className="sf-display text-[30px] leading-tight text-text-primary sm:text-[38px]" data-id="element-1735" data-mp="ewd8k85ukvaq">The problem</h2>
          <p className="text-base leading-relaxed text-text-secondary" data-id="element-1736" data-mp="9y3b1qwnhily">
            Investment teams see more companies than they can assess in depth. Screening is often inconsistent from one analyst to the
            next, information arrives in scattered documents, and a large share of time goes into assembling material rather than
            judging it.
          </p>
        </div>
      </section>

      <section aria-labelledby="approach-heading" className="border-b border-hairline-soft" data-id="element-1737" data-mp="sm8baoe3mlg0">
        <div className="mx-auto w-full max-w-[1100px] px-4 py-16 sm:px-6 lg:px-8" data-id="element-1738" data-mp="olwrfdtk8p5l">
          <h2 id="approach-heading" className="sf-display text-[30px] leading-tight text-text-primary sm:text-[38px]" data-id="element-1739" data-mp="3df20yhmrw18c">Our approach to diligence</h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-1740" data-mp="39f1k6qc7bdy4">
            {APPROACH.map(item => <li key={item.title} className="rounded-2xl border border-hairline bg-panel p-6" data-id="element-1741" data-mp="11261vvnchk8k">
                <h3 className="text-base font-semibold text-text-primary" data-id="element-1742" data-mp="15winoccan7nh">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-id="element-1743" data-mp="r3qadrkeo8yj">{item.body}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section aria-labelledby="team-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1744" data-mp="1jv04dq3s7jvw">
        <div className="mx-auto w-full max-w-[1100px] px-4 py-16 sm:px-6 lg:px-8" data-id="element-1745" data-mp="1ot1ngb17jj7d">
          <h2 id="team-heading" className="sf-display text-[30px] leading-tight text-text-primary sm:text-[38px]" data-id="element-1746" data-mp="1asqxda7q28n9">Team</h2>
          <div className="mt-8 flex flex-col gap-5 rounded-2xl border border-hairline bg-panel p-6 sm:flex-row sm:p-7" data-id="element-1747" data-mp="1etkixwxozbcz">
            <FounderAvatar data-id="element-1748" data-mp="3vmdvc8v06lzu" />
            <div data-id="element-1749" data-mp="3gqg0vzjhv52">
              <h3 className="text-lg font-semibold text-text-primary" data-id="element-1750" data-mp="3h9xkbla2hik">{FOUNDER.name}</h3>
              <p className="text-sm text-text-secondary" data-id="element-1751" data-mp="3s1aples1gmrh">{FOUNDER.role}</p>
              <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-text-secondary" data-id="element-1752" data-mp="3n30pqqvdcxzj">{FOUNDER.bio}</p>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="relationships-heading" className="border-b border-hairline-soft" data-id="element-1753" data-mp="5ssymmrgd05p">
        <div className="mx-auto w-full max-w-[1100px] px-4 py-16 sm:px-6 lg:px-8" data-id="element-1754" data-mp="tps0xvtqlvm">
          <h2 id="relationships-heading" className="sf-display text-[30px] leading-tight text-text-primary sm:text-[38px]" data-id="element-1755" data-mp="39c3zv7stuuw6">Who we work with</h2>
          <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-1756" data-mp="3j15g8z3abeao">Customers</h3>
          <ul className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-1757" data-mp="3ea5ixsq96u7n">
            {CUSTOMER_EVIDENCE.map(item => <li key={item.organization} className="rounded-xl border border-hairline bg-panel p-4" data-id="element-1758" data-mp="2qkb6vddrd9og">
                <p className="text-sm font-semibold text-text-primary" data-id="element-1759" data-mp="2lmt64a9wa0rj">{item.organization}</p>
                <p className="text-xs text-text-tertiary" data-id="element-1760" data-mp="3bayp5hr8rza2">{item.relationship}</p>
              </li>)}
          </ul>
          <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-1761" data-mp="3fa7hci6mvp3i">Pilots</h3>
          <p className="mt-3 text-sm text-text-secondary" data-id="element-1762" data-mp="3k38yyxy862qs">{PILOT_NOTE}</p>
        </div>
      </section>

      <section aria-labelledby="contact-heading" data-id="element-1763" data-mp="3p0sb9sbd45g3">
        <div className="mx-auto flex w-full max-w-[1100px] flex-col items-start gap-6 px-4 py-16 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8" data-id="element-1764" data-mp="2sbt7apht9ptp">
          <div data-id="element-1765" data-mp="2weprgkn01tzr">
            <h2 id="contact-heading" className="text-2xl font-semibold tracking-[-0.02em] text-text-primary" data-id="element-1766" data-mp="31c94jh7yo0q4">Talk to us about your workflow.</h2>
            <p className="mt-1 text-sm text-text-secondary" data-id="element-1767" data-mp="36bvhrqnshgyx">Book time with the team, or try an assessment yourself.</p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row" data-id="element-1768" data-mp="go4lex5b63wv">
            <CtaButton href={ENTERPRISE_DEMO_URL} event="enterprise_demo_click" eventParams={{
            placement: 'about'
          }} size="lg" data-id="element-1769" data-mp="lj0f01q70zc3">
              Book an Enterprise Demo
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1770" data-mp="213j8z82fhhda" />
            </CtaButton>
            <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
            placement: 'about',
            cta: 'get_started'
          }} variant="outline" size="lg" data-id="element-1771" data-mp="1wcauvyq5l7ts">
              Get Started
            </CtaButton>
          </div>
        </div>
      </section>
    </main>;
}